import { IContextualMenuItem } from "@fluentui/react";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { ContentManagerCopyMoveModeEnum } from "../../../src/taskpane/components/enums/ContentManagerCopyMoveModeEnum";
import { ComponentTypes } from "../../../src/taskpane/config/config";
import { RootState } from "../../../src/taskpane/redux";
import { setContentManagerMoveTo } from "../../../src/taskpane/redux/modules/contentManagerDirectory";
import { LanguageContext } from "../../../utilities/LocalizationModule";
import { menuItemIconStyles } from "../../../src/taskpane/config/controlStyles";
import { FolderArrowRight20Regular } from "../../../src/taskpane/components/_v2/components/SvgIcons/FolderArrowRight20Regular";
import { getFolderPermissionMenuState, runFolderPermissionMenuAction } from "./folderPermissionMenuState";

export function useMenuItem_MoveTo(itemCount: number | undefined, selectedItems: any[], setShowConfirmMoveToDialog: (isShowConfirmMoveToDialog: boolean) => void, isInOneDrive: boolean, isFetchingData: boolean) {
    const languageStrings = React.useContext(LanguageContext);

    const dispatch = useDispatch();

    const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories.oneDriveRootDetails);
    const workingDirectories = useSelector((state: RootState) => state.workingDirectories.workingDirectories);
    const moveToEnabled = useSelector((state: RootState) => state.contentManagerDirectory.moveTo.moveToEnabled);
    const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
    const moveToDirectory = useSelector((state: RootState) => state.contentManagerDirectory.moveTo.workingDirectories.length > 0 ? state.contentManagerDirectory.moveTo.workingDirectories[state.contentManagerDirectory.moveTo.workingDirectories.length - 1] : null);
    const moveToMode = useSelector((state: RootState) => state.contentManagerDirectory.moveTo.mode);
    const moveToBusyMovingFiles = useSelector((state: RootState) => state.contentManagerDirectory.moveTo.busyMovingFiles);
    const moveToBusyFetchingFiles = useSelector((state: RootState) => state.contentManagerDirectory.moveTo.busyFetchingFiles);
    const currentSharepointFolder = useSelector((state: RootState) => state.currentSharepointFolder);
    const tenantCopyMoveToEnabled = useSelector((state: RootState) => state.tenantInfo?.enableCopyMoveTo);
    const searchValue = useSelector((state: RootState) => state.contentManagerDirectory?.searchValue);
    const filterSections = useSelector((state: RootState) => state.contentManagerDirectory?.filterSections);

    const menuItem_MoveTo = React.useMemo(() => {
        const editPermissionMenuState = getFolderPermissionMenuState({
            currentSharepointFolder,
            requiredPermissions: ["canEditListItem"]
        });
        const deletePermissionMenuState = getFolderPermissionMenuState({
            currentSharepointFolder,
            requiredPermissions: ["canDeleteListItem"]
        });

        if (tenantCopyMoveToEnabled && itemCount > 0 && selectedItems.length === 0 && !moveToEnabled && currentDirectory.type !== ComponentTypes.SharePointList) {
            const isFilterActive = filterSections?.some(section =>
                section.items?.some(item => item.selected === true) ||
                section.daysOffset ||
                section.daysOffset === 0
            );

            const isSearchActive = searchValue && (searchValue.value || searchValue.filterType !== "");

            if (isFilterActive || isSearchActive) {
                return null;
            }

            if (!deletePermissionMenuState.shouldRender) {
                return null;
            }

            return {
                key: "moveTo",
                text: `${languageStrings.MoveAll}`,
                disabled: deletePermissionMenuState.isDisabled || isFetchingData || moveToBusyMovingFiles || moveToBusyFetchingFiles,
                // iconProps: { iconName: "FabricMovetoFolder" },
                iconProps: {},
                onRenderIcon: () => <FolderArrowRight20Regular className={menuItemIconStyles} />,
                onClick: () => {
                    runFolderPermissionMenuAction(deletePermissionMenuState, () => {
                        const sourceSiteUrl = isInOneDrive ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl;

                        dispatch(setContentManagerMoveTo({
                            mode: ContentManagerCopyMoveModeEnum.MoveAll,
                            moveToEnabled: false,
                            workingDirectories: workingDirectories,
                            selectedFiles: [],
                            siteUrl: "",
                            sourceSiteUrl,
                            sourceServerRelPath: "",
                            busyMovingFiles: false,
                            copyJobResults: [],
                            isMoveComplete: false,
                            isMoveCancelled: false,
                            destinationDirectory: {},
                            progressCountCompleted: 0,
                            progressCountTotal: 0,
                            error: ""
                        }));

                        setShowConfirmMoveToDialog(true);
                    });
                }
            } as IContextualMenuItem;
        }
        else if (tenantCopyMoveToEnabled
            && !moveToEnabled
            && itemCount > 0
            && selectedItems.length > 0
            && !selectedItems.some(x => x?.EffectiveBasePermissions?.canDeleteListItem === false)
            && currentDirectory.type !== ComponentTypes.SharePointList) {
            return {
                key: "moveTo",
                text: `${languageStrings.MoveTo}`,
                // iconProps: { iconName: "FabricMovetoFolder" },
                iconProps: {},
                onRenderIcon: () => <FolderArrowRight20Regular className={menuItemIconStyles} />,
                onClick: () => {
                    const sourceSiteUrl = isInOneDrive ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl;

                    dispatch(setContentManagerMoveTo({
                        mode: ContentManagerCopyMoveModeEnum.Move,
                        moveToEnabled: false,
                        workingDirectories: workingDirectories,
                        selectedFiles: selectedItems,
                        siteUrl: "",
                        sourceSiteUrl,
                        sourceServerRelPath: "",
                        busyMovingFiles: false,
                        copyJobResults: [],
                        isMoveComplete: false,
                        isMoveCancelled: false,
                        isMoveInProgress: false,
                        destinationDirectory: {},
                        progressCountCompleted: 0,
                        progressCountTotal: 0,
                        error: ""
                    }));

                    setShowConfirmMoveToDialog(true);
                }
            } as IContextualMenuItem;
        } else if (moveToEnabled && (moveToMode === ContentManagerCopyMoveModeEnum.Move || moveToMode === ContentManagerCopyMoveModeEnum.MoveAll)) {
            if (!editPermissionMenuState.shouldRender) {
                return null;
            }

            return {
                key: "moveTo",
                text: `${languageStrings.Confirm} ${languageStrings.MoveTo}`,
                disabled: currentDirectory?.id === moveToDirectory?.id || editPermissionMenuState.isDisabled || isFetchingData,
                // iconProps: { iconName: "FabricMovetoFolder" },
                iconProps: {},
                onRenderIcon: () => <FolderArrowRight20Regular className={menuItemIconStyles} />,
                onClick: () => {
                    runFolderPermissionMenuAction(editPermissionMenuState, () => {
                        setShowConfirmMoveToDialog(true);
                    });
                }
            } as IContextualMenuItem;
        }
        return null;
    }, [itemCount, selectedItems, moveToEnabled, currentDirectory, moveToDirectory, moveToMode, isInOneDrive, workingDirectories, isFetchingData, moveToBusyMovingFiles, moveToBusyFetchingFiles, currentSharepointFolder, filterSections, searchValue, tenantCopyMoveToEnabled, languageStrings, dispatch, oneDriveRootDetails, setShowConfirmMoveToDialog])

    return {
        menuItem_MoveTo
    }
}
